import { Controller } from "@hotwired/stimulus"

// Reusable drag-and-drop board controller — Asana-quality.
// Works for tasks, deals, or any model with board columns.
//
// Values
//   update-url  – URL template with :id placeholder, e.g. "/tasks/:id"
//   item-param  – top-level param key sent in JSON body, e.g. "task"
//   field       – the attribute being changed, e.g. "board_column_id"

export default class extends Controller {
  static targets = ["column", "list", "card", "columnTitle", "columnMenu", "inlineForm", "inlineInput", "addColumnForm", "addColumnInput"]
  static values = {
    updateUrl:  String,
    itemParam:  String,
    field:      String
  }

  connect() {
    this.draggedCard = null
    this.placeholder = null
    this.setupDragAndDrop()
    this.setupColumnEditing()
  }

  // ── Setup ────────────────────────────────────────────────

  setupDragAndDrop() {
    this.cardTargets.forEach(card => {
      card.setAttribute("draggable", "true")
      card.addEventListener("dragstart", this.onDragStart.bind(this))
      card.addEventListener("dragend", this.onDragEnd.bind(this))
    })

    this.listTargets.forEach(list => {
      list.addEventListener("dragover", this.onDragOver.bind(this))
      list.addEventListener("dragenter", this.onDragEnter.bind(this))
      list.addEventListener("dragleave", this.onDragLeave.bind(this))
      list.addEventListener("drop", this.onDrop.bind(this))
    })
  }

  setupColumnEditing() {
    this.columnTitleTargets.forEach(title => {
      title.addEventListener("blur", this.onColumnTitleBlur.bind(this))
      title.addEventListener("keydown", (e) => {
        if (e.key === "Enter") { e.preventDefault(); title.blur() }
        if (e.key === "Escape") { title.blur() }
      })
    })
  }

  // ── Drag Events ──────────────────────────────────────────

  onDragStart(event) {
    this.draggedCard = event.currentTarget
    this.draggedCard.classList.add("board-card--dragging")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.draggedCard.dataset.itemId)

    // Create placeholder
    this.placeholder = document.createElement("div")
    this.placeholder.classList.add("board-card-placeholder")
    this.placeholder.style.height = `${this.draggedCard.offsetHeight}px`

    // Slight delay so the drag image captures properly
    requestAnimationFrame(() => {
      this.draggedCard.style.opacity = "0.4"
    })
  }

  onDragEnd() {
    if (this.draggedCard) {
      this.draggedCard.classList.remove("board-card--dragging")
      this.draggedCard.style.opacity = ""
    }
    if (this.placeholder?.parentNode) {
      this.placeholder.remove()
    }
    this.columnTargets.forEach(col => col.classList.remove("board-column--drop-target"))
    this.draggedCard = null
    this.placeholder = null
  }

  onDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const list = event.currentTarget
    const afterElement = this.getDragAfterElement(list, event.clientY)

    if (!this.placeholder.parentNode || this.placeholder.parentNode !== list) {
      if (afterElement) {
        list.insertBefore(this.placeholder, afterElement)
      } else {
        list.appendChild(this.placeholder)
      }
    } else if (afterElement) {
      list.insertBefore(this.placeholder, afterElement)
    } else {
      list.appendChild(this.placeholder)
    }
  }

  onDragEnter(event) {
    event.preventDefault()
    const column = event.currentTarget.closest("[data-board-target='column']")
    column?.classList.add("board-column--drop-target")
  }

  onDragLeave(event) {
    const list = event.currentTarget
    const rect = list.getBoundingClientRect()
    if (
      event.clientX < rect.left || event.clientX > rect.right ||
      event.clientY < rect.top || event.clientY > rect.bottom
    ) {
      const column = list.closest("[data-board-target='column']")
      column?.classList.remove("board-column--drop-target")
    }
  }

  onDrop(event) {
    event.preventDefault()
    const list = event.currentTarget
    const column = list.closest("[data-board-target='column']")
    if (!column) return

    column.classList.remove("board-column--drop-target")

    const itemId = event.dataTransfer.getData("text/plain")
    const newValue = column.dataset.columnKey
    const card = this.element.querySelector(`[data-item-id='${itemId}']`)

    if (card && list) {
      if (this.placeholder?.parentNode) {
        this.placeholder.replaceWith(card)
      } else {
        list.appendChild(card)
      }

      card.style.opacity = ""
      card.classList.remove("board-card--dragging")
      this.updateColumnCounts()
      this.persistChange(itemId, newValue, card)
    }
  }

  // ── Column Management ────────────────────────────────────

  toggleColumnMenu(event) {
    const columnId = event.currentTarget.dataset.columnId
    const menu = this.columnMenuTargets.find(m => m.dataset.columnId === columnId)
    if (menu) {
      const isHidden = menu.classList.contains("hidden")
      // Close all menus first
      this.columnMenuTargets.forEach(m => m.classList.add("hidden"))
      if (isHidden) menu.classList.remove("hidden")
    }
    // Close on outside click
    const closeMenu = (e) => {
      if (!e.target.closest(".board-column-actions")) {
        this.columnMenuTargets.forEach(m => m.classList.add("hidden"))
        document.removeEventListener("click", closeMenu)
      }
    }
    setTimeout(() => document.addEventListener("click", closeMenu), 0)
  }

  onColumnTitleBlur(event) {
    const title = event.currentTarget
    const columnId = title.dataset.columnId
    const newName = title.textContent.trim()
    if (!newName) return

    this.patchColumn(columnId, { name: newName })
  }

  editColumnColor(event) {
    const columnId = event.currentTarget.dataset.columnId
    const colors = ["#6b7280", "#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#22c55e", "#ef4444", "#06b6d4", "#f97316"]
    const picker = document.createElement("div")
    picker.className = "board-color-picker"
    picker.innerHTML = colors.map(c =>
      `<button class="board-color-swatch" style="background:${c}" data-color="${c}"></button>`
    ).join("")
    event.currentTarget.closest(".board-column-menu").innerHTML = ""
    event.currentTarget.closest(".board-column-menu").appendChild(picker)
    event.currentTarget.closest(".board-column-menu").classList.remove("hidden")

    picker.addEventListener("click", (e) => {
      const swatch = e.target.closest("[data-color]")
      if (!swatch) return
      this.patchColumn(columnId, { color: swatch.dataset.color })
      // Update the dot
      const col = this.columnTargets.find(c => c.dataset.columnKey === columnId)
      if (col) col.querySelector(".board-column-dot").style.backgroundColor = swatch.dataset.color
      picker.closest(".board-column-menu").classList.add("hidden")
    })
  }

  setWipLimit(event) {
    const columnId = event.currentTarget.dataset.columnId
    const limit = prompt("Set WIP limit (leave empty to remove):")
    if (limit === null) return
    this.patchColumn(columnId, { wip_limit: limit === "" ? null : parseInt(limit) })
  }

  async deleteColumn(event) {
    const columnId = event.currentTarget.dataset.columnId
    const col = this.columnTargets.find(c => c.dataset.columnKey === columnId)
    const name = col?.dataset.columnName || "this column"
    if (!confirm(`Delete "${name}"? Tasks will be moved to the first column.`)) return

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const response = await fetch(`/board_columns/${columnId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" }
      })
      if (response.ok) {
        col?.remove()
        this.updateColumnCounts()
      } else {
        console.error(`Failed to delete column: HTTP ${response.status}`)
        alert(`Could not delete "${name}". Please try again.`)
      }
    } catch (err) {
      console.error("Failed to delete column:", err)
      alert(`Could not delete "${name}". Please check your connection.`)
    }
  }

  async patchColumn(columnId, data) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const response = await fetch(`/board_columns/${columnId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ board_column: data })
      })
      if (!response.ok) {
        console.error(`Failed to update column: HTTP ${response.status}`)
      }
    } catch (err) {
      console.error("Failed to update column:", err)
    }
  }

  // ── Inline Add Task ──────────────────────────────────────

  showInlineAdd(event) {
    const columnId = event.currentTarget.dataset.columnId
    const form = this.inlineFormTargets.find(f => f.dataset.columnId === columnId)
    if (form) {
      event.currentTarget.classList.add("hidden")
      form.classList.remove("hidden")
      form.querySelector("input[type='text']").focus()
    }
  }

  hideInlineAdd(event) {
    const form = event.currentTarget.closest(".board-inline-add")
    if (form) {
      form.classList.add("hidden")
      form.previousElementSibling?.classList.remove("hidden")
    }
  }

  async createInlineTask(event) {
    event.preventDefault()
    const form = event.currentTarget
    const input = form.querySelector("input[type='text']")
    const columnId = form.querySelector("input[name='board_column_id']").value
    const title = input.value.trim()
    if (!title) return

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const response = await fetch("/tasks", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ task: { title, board_column_id: columnId } })
      })

      if (response.ok) {
        // Reload to show the new card (simplest approach)
        window.location.reload()
      }
    } catch (error) {
      console.error("Failed to create task:", error)
    }

    input.value = ""
  }

  // ── Add Column ───────────────────────────────────────────

  showAddColumn() {
    const form = this.addColumnFormTarget
    form.classList.remove("hidden")
    form.previousElementSibling?.classList.add("hidden")
    this.addColumnInputTarget.focus()
  }

  hideAddColumn() {
    const form = this.addColumnFormTarget
    form.classList.add("hidden")
    form.previousElementSibling?.classList.remove("hidden")
  }

  async createColumn(event) {
    event.preventDefault()
    const name = this.addColumnInputTarget.value.trim()
    if (!name) return

    const columnType = event.currentTarget.dataset.columnType || "task"
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const submitBtn = event.currentTarget.querySelector("button[type='submit']")
    const originalText = submitBtn?.textContent

    // Disable form during submission
    if (submitBtn) {
      submitBtn.textContent = "Adding..."
      submitBtn.disabled = true
    }

    try {
      const response = await fetch("/board_columns", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ board_column: { name, column_type: columnType, color: "#6b7280" } })
      })

      if (response.ok) {
        window.location.reload()
      } else {
        const data = await response.json().catch(() => ({}))
        const message = data.errors?.join(", ") || `Failed to create column (${response.status})`
        console.error("Column creation failed:", message)

        // Show inline error
        let errorEl = event.currentTarget.querySelector(".board-inline-error")
        if (!errorEl) {
          errorEl = document.createElement("p")
          errorEl.className = "board-inline-error"
          errorEl.style.cssText = "color: #ef4444; font-size: 0.75rem; margin-top: 0.25rem;"
          event.currentTarget.appendChild(errorEl)
        }
        errorEl.textContent = message

        if (submitBtn) {
          submitBtn.textContent = originalText
          submitBtn.disabled = false
        }
      }
    } catch (error) {
      console.error("Failed to create column:", error)
      if (submitBtn) {
        submitBtn.textContent = originalText
        submitBtn.disabled = false
      }
    }
  }

  // ── Helpers ──────────────────────────────────────────────

  getDragAfterElement(list, y) {
    const draggableElements = [
      ...list.querySelectorAll("[data-item-id]:not(.board-card--dragging)")
    ]

    let closest = null
    let closestOffset = Number.NEGATIVE_INFINITY

    draggableElements.forEach(child => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2
      if (offset < 0 && offset > closestOffset) {
        closestOffset = offset
        closest = child
      }
    })

    return closest
  }

  updateColumnCounts() {
    this.columnTargets.forEach(column => {
      const count = column.querySelectorAll("[data-item-id]").length
      const badge = column.querySelector("[data-count]")
      if (badge) badge.textContent = count
    })
  }

  async persistChange(itemId, newValue, card) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const url = this.updateUrlValue.replace(":id", itemId)
    const body = {}
    body[this.itemParamValue] = {}
    body[this.itemParamValue][this.fieldValue] = newValue

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      // Success animation
      card?.classList.add("board-card--success")
      setTimeout(() => card?.classList.remove("board-card--success"), 1000)
    } catch (error) {
      console.error(`Failed to update ${this.itemParamValue}:`, error)
      card?.classList.add("board-card--error")
      setTimeout(() => card?.classList.remove("board-card--error"), 2000)
    }
  }
}
